import { Link } from '@tanstack/react-router'
import { format } from 'date-fns'
import { ArrowLeftIcon, ArrowRightIcon } from 'lucide-react'

import { useDocumentTitle } from '@/hooks'
import { cn } from '@/utils'
import type { UpdateLink } from '../service/changelog'
import type { Update } from '../types'
import { StructuredText } from './StructuredText'
import { UpdateKindBadge } from './UpdateKindBadge'

const BackLink = () => (
  <Link
    to="/updates"
    preload="intent"
    className="inline-flex items-center gap-1.5 rounded text-pretty text-sm font-medium text-blue-6 outline-none transition-colors cursor-pointer hover:text-foreground focus-visible:ring-2 focus-visible:ring-brand/30"
  >
    <ArrowLeftIcon className="h-4 w-4" />
    Updates
  </Link>
)

const NavCard = ({
  entry,
  direction,
}: {
  entry: UpdateLink
  direction: 'previous' | 'next'
}) => {
  const isNext = direction === 'next'
  return (
    <Link
      to="/updates/$slug"
      params={{ slug: entry.slug }}
      preload="intent"
      className={cn(
        'group flex flex-col gap-1 rounded-lg border p-4 outline-none transition-colors cursor-pointer hover:border-brand/50 focus-visible:ring-2 focus-visible:ring-brand/40',
        isNext && 'items-end text-right',
      )}
    >
      <span className="flex items-center gap-1 text-pretty text-xs font-medium text-blue-6">
        {isNext ? (
          <>
            Next
            <ArrowRightIcon className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
          </>
        ) : (
          <>
            <ArrowLeftIcon className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
            Previous
          </>
        )}
      </span>
      <span className="line-clamp-1 text-pretty text-sm font-semibold text-foreground group-hover:text-brand">
        {entry.title}
      </span>
    </Link>
  )
}

interface UpdateDetailProps {
  update: Update | null
  previous: UpdateLink | null
  next: UpdateLink | null
}

export const UpdateDetail = ({ update, previous, next }: UpdateDetailProps) => {
  useDocumentTitle(update?.title ?? 'Updates')

  if (!update) {
    return (
      <div className="mx-auto max-w-2xl space-y-6">
        <BackLink />
        <p className="text-pretty text-sm text-blue-6">
          This update could not be found. It may have been unpublished.
        </p>
      </div>
    )
  }

  return (
    <article className="mx-auto max-w-2xl space-y-6">
      <BackLink />

      <header className="space-y-3">
        <div className="flex items-center gap-3">
          <UpdateKindBadge kind={update.kind} />
          <span className="text-pretty text-xs font-medium text-blue-6 tabular-nums">
            {format(new Date(update.publishedAt), 'MMMM d, yyyy')}
          </span>
        </div>
        <h1 className="text-pretty text-2xl font-bold tracking-tight text-foreground">
          {update.title}
        </h1>
      </header>

      {update.body && <StructuredText document={update.body} />}

      {(previous || next) && (
        <nav className="grid grid-cols-1 gap-3 border-t pt-6 sm:grid-cols-2">
          {previous ? (
            <NavCard entry={previous} direction="previous" />
          ) : (
            <span />
          )}
          {next ? <NavCard entry={next} direction="next" /> : <span />}
        </nav>
      )}
    </article>
  )
}
